import type { FailCodeSubsectionEntity } from '../entities';

import { StsBackendOperation } from '../../sts-backend-operation';

/**
 * Parameters for fetching fail code subsections filtered by a specific fail code and section.
 */
export type FetchSubsectionsByFailCodeParams = {
    /** The fail code to filter subsections by. */
    failCode?: string;

    /** The vehicle model code (e.g. "GUE"). */
    modelCode?: string;

    /** The vehicle model year (e.g. "2024"). */
    modelYear?: string;

    /** The vehicle option/trim code (e.g. "01"). */
    optionCode?: string;

    /** The display name of the parent section. */
    sectionName: string;

    /** The numeric identifier of the parent section. */
    sectionNumber: number;

    /** Optional abort signal for cancellation. */
    signal?: AbortSignal;
};

/**
 * Fetch fail code subsections for a given section, filtered by a specific fail code.
 * Used in the search-by-fail-code flow when the user manually selects a section after
 * the initial resolution returned multiple sections.
 */
export class FetchSubsectionsByFailCode extends StsBackendOperation {
    /**
     * Fetch subsections from the API and return them as domain entities.
     *
     * @param params - the fail code, vehicle identifiers, parent section, and optional abort signal
     * @returns the matching subsections as domain entities
     */
    async exec(params: FetchSubsectionsByFailCodeParams): Promise<FailCodeSubsectionEntity[]> {
        const { signal, ...request } = params;
        const response = await this.client.fetchPossibleSubsections(request, signal);

        if (!response.success) {
            return [];
        }

        return response.data.subsections ?? [];
    }
}
